import { useEffect, useState } from 'react';
import { type Control, useWatch, useFormState } from 'react-hook-form';
import { useDebounce } from '@/hooks/use-debounce';
import { useVatValidationMutation } from '@/state/mutations/vat-validation/use-vat-validation';
import { useTranslations } from '@/i18n/use-translations';
import type { EditActiveOrganization } from '../../edit.modal';
import type { ClearErrorsFunction } from '@/types/form';

type VatValidationStatus = 'verified' | 'unverified' | 'error' | 'pending';

const getVatVerifiedColor = (status: VatValidationStatus): string => {
  switch (status) {
    case 'verified':
      return 'success.main';
    case 'error':
      return 'error.main';
    case 'pending':
      return 'active.main';
    default:
      return 'disabled.main';
  }
};

export const useVatValidation = ({
  control,
  clearErrors,
  wasDirty,
}: {
  control: Control<EditActiveOrganization>;
  clearErrors: ClearErrorsFunction<EditActiveOrganization>;
  wasDirty: boolean;
}) => {
  const { mutateAsync: vatValidationMutation } = useVatValidationMutation();
  const [vatValidationStatus, setVatValidationStatus] = useState<VatValidationStatus>('unverified');
  const { errors } = useFormState({ control });
  const t = useTranslations();

  const vat = useWatch({
    control,
    name: 'billingAddress.vat',
    defaultValue: '',
  });
  const country = useWatch({
    control,
    name: 'billingAddress.country',
    defaultValue: '',
  });

  const debouncedVat = useDebounce(vat);

  useEffect(() => {
    (async () => {
      // Early checks before async validation
      if (!wasDirty) {
        setVatValidationStatus('verified');
        return;
      }
      if (!debouncedVat && !country) {
        setVatValidationStatus('unverified');
        return;
      }

      const hasNonApiError = errors.billingAddress?.vat && errors.billingAddress.vat.type !== 'validation';
      if (hasNonApiError) {
        setVatValidationStatus('unverified');
        return;
      }

      setVatValidationStatus('pending');
      try {
        const countryValue = control._formValues?.billingAddress?.country || '';
        const response = await vatValidationMutation({ vat: debouncedVat!, country: countryValue });
        if (response.isValid) {
          clearErrors('billingAddress.vat');
          setVatValidationStatus('verified');
        } else {
          setVatValidationStatus('error');
        }
      } catch {
        setVatValidationStatus('error');
      }
    })();
  }, [vat, debouncedVat, country, vatValidationMutation, wasDirty, errors.billingAddress?.vat, control, clearErrors]);

  useEffect(() => {
    const hasApiValidatedError = vat && vatValidationStatus === 'error';
    const hasApiValidatedSuccess = vat && vatValidationStatus === 'verified';
    const hasValidationError = errors?.billingAddress?.vat && errors.billingAddress.vat.type === 'validation';

    if (!errors?.billingAddress?.vat && hasApiValidatedError) {
      control.setError('billingAddress.vat', {
        type: 'validation',
        message: t('organization.validation.vatInvalid'),
      });
    }

    // Clear validation error when VAT is successfully validated
    if (hasValidationError && hasApiValidatedSuccess) {
      clearErrors('billingAddress.vat');
    }
  }, [vatValidationStatus, vat, control, errors]);

  const vatVerifiedColor = getVatVerifiedColor(vatValidationStatus);

  return {
    vatVerifiedColor,
  };
};
